import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import type { BrandComposeStatus, BrandTemplateProposal } from "@/api/types";

// ---- Org brand composer ("Plan C") ----
//
// Kick off an async agent run that inspects dropped brand assets + a
// reference URL + a free-text brief, then proposes a set of token /
// asset changes. The card POSTs the inputs (multipart), receives a
// ``compose_id``, polls the job, and — once ``ready`` — lets the
// operator merge (apply) or discard the proposal.

const brandComposeKey = (slug: string, jobId: string) =>
  ["orgs", slug, "brand", "compose", jobId] as const;

/** Start a brand-compose run. Multipart payload mirrors
 *  ``useUploadBrandAsset`` / ``useImportBrandBundle``: repeated
 *  ``files`` parts plus ``url`` / ``prompt`` / ``provider`` /
 *  optional ``model`` fields. Resolves to the ``compose_id`` the
 *  caller polls with ``useBrandComposeJob``.
 *
 *  ``model`` is an optional operator override picked from the
 *  composer's model dropdown (added for parity with the
 *  new-session form). Empty / undefined → the backend resolves
 *  the per-user default through the same dynamic catalog the
 *  new-session form's picker uses, so the composer's default
 *  matches what the operator would see pre-selected there. */
export function useComposeBrand(slug: string) {
  return useMutation({
    mutationFn: async (input: {
      files: File[];
      url: string;
      prompt: string;
      provider: string;
      model?: string;
      // ``update`` merges into ``target_profile_id`` (omitted → the
      // backend defaults to the org primary); ``new`` creates a profile
      // named ``target_profile_name`` when the proposal is applied.
      target_mode?: "update" | "new";
      target_profile_id?: string | null;
      target_profile_name?: string;
    }): Promise<{ compose_id: string }> => {
      const fd = new FormData();
      for (const file of input.files) fd.append("files", file);
      fd.append("url", input.url);
      fd.append("prompt", input.prompt);
      fd.append("provider", input.provider);
      if (input.model && input.model.length > 0) {
        fd.append("model", input.model);
      }
      const mode = input.target_mode ?? "update";
      fd.append("target_mode", mode);
      if (mode === "update" && input.target_profile_id) {
        fd.append("target_profile_id", input.target_profile_id);
      }
      if (mode === "new") {
        fd.append("target_profile_name", (input.target_profile_name ?? "").trim());
      }
      const r = await api.post<{ compose_id: string }>(
        `/orgs/${slug}/brand/compose`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return r.data;
    },
  });
}

/** Fetch the org's most-recent compose job (or null) so the composer
 *  card can reattach after the operator leaves and returns to the
 *  Branding view — resuming live progress for a running job or
 *  re-surfacing a ``ready`` proposal to apply. Polls while the latest
 *  job is still in flight so a freshly-returned operator sees progress
 *  without a manual refresh. */
export function useLatestBrandCompose(slug: string) {
  return useQuery({
    queryKey: ["orgs", slug, "brand", "compose", "latest"] as const,
    queryFn: async () =>
      (
        await api.get<BrandComposeStatus | null>(
          `/orgs/${slug}/brand/compose/latest`,
        )
      ).data,
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s === "queued" || s === "running" ? 2000 : false;
    },
    staleTime: 0,
  });
}

/** Poll a brand-compose job. Enabled only when a ``jobId`` is set;
 *  refetches every 2 s while ``queued`` / ``running`` and stops once
 *  the job reaches a terminal state (``ready`` / ``failed``).
 *  ``staleTime: 0`` so a remounted card always sees the live status
 *  rather than a cached snapshot. */
export function useBrandComposeJob(slug: string, jobId: string | null) {
  return useQuery({
    queryKey: brandComposeKey(slug, jobId ?? ""),
    enabled: Boolean(jobId),
    queryFn: async () =>
      (
        await api.get<BrandComposeStatus>(
          `/orgs/${slug}/brand/compose/${jobId}`,
        )
      ).data,
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s === "queued" || s === "running" ? 2000 : false;
    },
    staleTime: 0,
  });
}

/** What an apply wrote and where: ``profile_id`` / ``profile_name``
 *  name the profile the proposal landed in (a ``new`` target only
 *  exists from this moment). */
export type BrandComposeApplyResult = {
  tokens: number;
  assets: number;
  profile_id: string;
  profile_name: string;
};

/** Merge a ready proposal into the job's target profile (creating it
 *  for a ``new`` target). Refreshes the org's whole brand subtree: the
 *  target's tokens / assets / preview changed, a new profile may exist,
 *  and the cached ``latest`` job must not re-resume the consumed
 *  proposal. */
export function useApplyBrandCompose(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (jobId: string) =>
      (
        await api.post<BrandComposeApplyResult>(
          `/orgs/${slug}/brand/compose/${jobId}/apply`,
        )
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orgs", slug, "brand"] });
    },
  });
}

/** Read an uploaded Office template into a brand proposal. Stores
 *  nothing: the same file goes to ``useApplyBrandTemplate``. */
export function usePreviewBrandTemplate(slug: string) {
  return useMutation({
    mutationFn: async (file: File): Promise<BrandTemplateProposal> => {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post<BrandTemplateProposal>(
        `/orgs/${slug}/brand/template-import`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return r.data;
    },
  });
}

/** Apply a template's proposal: the server reads ``file`` again and
 *  merges what it carries, minus ``exclude`` (token keys and asset
 *  kinds), into a new profile or the open one. ``expected_sha256`` (the
 *  preview's) makes it refuse a different file. Refreshes the org's
 *  whole brand subtree: a profile may be new, and its tokens, assets
 *  and preview changed. */
export function useApplyBrandTemplate(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      file: File;
      target_mode: "update" | "new";
      target_profile_id?: string | null;
      target_profile_name?: string;
      exclude: string[];
      expected_sha256: string;
    }): Promise<BrandComposeApplyResult> => {
      const fd = new FormData();
      fd.append("file", input.file);
      fd.append("target_mode", input.target_mode);
      if (input.target_mode === "update" && input.target_profile_id) {
        fd.append("target_profile_id", input.target_profile_id);
      }
      if (input.target_mode === "new") {
        fd.append("target_profile_name", (input.target_profile_name ?? "").trim());
      }
      for (const key of input.exclude) fd.append("exclude", key);
      fd.append("expected_sha256", input.expected_sha256);
      const r = await api.post<BrandComposeApplyResult>(
        `/orgs/${slug}/brand/template-import/apply`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orgs", slug, "brand"] });
    },
  });
}

/** Dismiss a ready proposal without merging. Persists a terminal
 *  ``discarded`` status server-side so the proposal stops re-surfacing on
 *  the next ``/latest`` poll / page reload (the bug was that Discard was
 *  client-only). Invalidates ``latest`` so the card drops it immediately. */
export function useDiscardBrandCompose(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (jobId: string): Promise<void> => {
      await api.post(`/orgs/${slug}/brand/compose/${jobId}/discard`);
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["orgs", slug, "brand", "compose", "latest"] as const,
      });
    },
  });
}
